// Runtime config loader.
// Replaces the old build-time VITE_ENVIRONMENT switch so one portal image
// can run anywhere — config comes from /config.json, which nginx serves
// from /tmp/config.json (written at container start by the entrypoint).
//
// Usage:
//   1. main.tsx calls `await loadRuntimeConfig()` BEFORE mounting React.
//   2. Any module can then `import { getConfig } from "./runtimeConfig"`
//      and read values synchronously.
//   3. Calling getConfig() before loadRuntimeConfig() completes throws —
//      an intentional guard so config can never be silently empty.

export type EnvType = "local" | "replit" | "dev" | "qa" | "stg" | "prod" | "production";

export interface RuntimeConfig {
  environment: EnvType;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  authServiceUrl: string;
  lauraServiceUrl: string;
  earlyAccessApiUrl: string;
  projectsServiceUrl: string;
  testRunnerServiceUrl: string;
  websocketUrl: string;
  multiChatWebsocketUrl: string;
  chatServiceUrl: string;
  chatTestsServiceUrl: string;
  managerServiceUrl: string;
  // Public marketing site. Used by RootRedirect to bounce unauth
  // visitors from `/` to the landing page when it lives in its own
  // service (www.assureagent.ai). Optional — empty disables the bounce.
  landingUrl: string;
}

/**
 * Fallback used when /config.json can't be fetched (typically during
 * `vite` dev mode without deploy/local/fetch-env.sh having run first).
 * Points at the assureagent local layout: one auth-service on :3001
 * serving both auth and /earlyAccess routes. For a real local setup,
 * prefer running `bash deploy/local/fetch-env.sh` which writes a proper
 * portal/public/config.json with dev-env Auth0 + backend URLs.
 */
const DEV_FALLBACK: RuntimeConfig = {
  environment: "local",
  auth0Domain: "dev-e3t50onrrrq4i3ua.us.auth0.com",
  auth0ClientId: "E4SYRxaUyVCKXk7FFPQutwsZXihhFUNp",
  auth0Audience: "https://api-dev.assureagent.ai",
  authServiceUrl: "http://localhost:3001",
  lauraServiceUrl: "",
  earlyAccessApiUrl: "http://localhost:3001/earlyAccess",
  projectsServiceUrl: "http://localhost:8080",
  testRunnerServiceUrl: "http://localhost:3000",
  websocketUrl: "ws://localhost:8080",
  multiChatWebsocketUrl: "",
  chatServiceUrl: "http://localhost:8090",
  chatTestsServiceUrl: "http://localhost:3010",
  managerServiceUrl: "http://localhost:8091",
  landingUrl: "http://localhost:5174",
};

let _config: RuntimeConfig | null = null;
let _loadPromise: Promise<RuntimeConfig> | null = null;

// http://host → ws://host, https://host → wss://host.
// Trailing slash is preserved or stripped — wouter / WebSocket APIs
// both tolerate either, so we keep things untouched beyond the protocol.
function deriveWsUrl(httpUrl: string): string {
  try {
    const u = new URL(httpUrl);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return u.toString().replace(/\/$/, '');
  } catch {
    return httpUrl;
  }
}

/**
 * Fetch /config.json once and cache the parsed result. Safe to call
 * multiple times — concurrent callers share the same in-flight promise.
 */
export function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (_config) return Promise.resolve(_config);
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    try {
      const res = await fetch("/config.json", { cache: "no-store" });
      if (!res.ok) {
        throw new Error(`/config.json returned ${res.status}`);
      }
      const parsed = (await res.json()) as Partial<RuntimeConfig>;
      // Merge fallback under the fetched values so empty strings from the
      // entrypoint (unset env vars) surface as empty, not as dev defaults.
      _config = { ...DEV_FALLBACK, ...parsed } as RuntimeConfig;
      // V2's runtime config (docker-entrypoint.d/10-write-runtime-config.sh)
      // doesn't ship `websocketUrl` — that field exists only in V1's
      // config shape. The Scenarios designer talks WebSocket to the
      // SAME host as projects-service, so derive it here if absent.
      // Without this, V2 falls back to `ws://localhost:8080` from
      // DEV_FALLBACK and every deployed user hits a dead socket.
      if (!parsed.websocketUrl && _config.projectsServiceUrl) {
        _config.websocketUrl = deriveWsUrl(_config.projectsServiceUrl);
      }
      return _config;
    } catch (err) {
      // Vite dev server (`npm run dev`) doesn't serve /config.json. Fall
      // back to local defaults and warn loudly so devs notice.
      console.warn(
        "[runtimeConfig] failed to fetch /config.json — using dev fallback:",
        err,
      );
      _config = { ...DEV_FALLBACK };
      return _config;
    }
  })();

  return _loadPromise;
}

/**
 * Synchronous accessor. Only safe after loadRuntimeConfig() has resolved
 * at least once. Throwing is deliberate — we'd rather fail loudly than
 * silently ship requests to empty base URLs.
 */
export function getConfig(): RuntimeConfig {
  if (!_config) {
    throw new Error(
      "runtime config not loaded — call loadRuntimeConfig() during bootstrap before importing modules that read config",
    );
  }
  return _config;
}
